import { ReactNode, useState, useEffect, useRef } from 'react'
import Modal from './Modal'

interface ResponsiveTableProps {
  children: ReactNode
  className?: string
}

/**
 * A responsive table wrapper that provides:
 * - Smart column hiding based on available width
 * - Progressively hides columns to keep actions column visible
 * - "View Details" button to show hidden columns in a modal
 *
 * Usage:
 * <ResponsiveTable>
 *   <table className="table">
 *     <thead>
 *       <tr>
 *         <th>Name</th>
 *         <th className="hide-mobile" data-priority="3">Email</th>
 *         <th className="hide-mobile" data-priority="2">Type</th>
 *         <th className="hide-mobile" data-priority="1">Location</th>
 *         <th>Actions</th>
 *       </tr>
 *     </thead>
 *     <tbody>
 *       <tr>
 *         <td>John Doe</td>
 *         <td className="hide-mobile" data-label="Email" data-priority="3">john@example.com</td>
 *         <td className="hide-mobile" data-label="Type" data-priority="2">Admin</td>
 *         <td className="hide-mobile" data-label="Location" data-priority="1">NYC</td>
 *         <td>
 *           <button className="mobile-details-trigger">Details</button>
 *           <button>Edit</button>
 *         </td>
 *       </tr>
 *     </tbody>
 *   </table>
 * </ResponsiveTable>
 *
 * Add "hide-mobile" class to columns you want to be hideable
 * Add data-priority attribute (1=hide first, 2=hide second, etc.) to control hiding order
 * Add data-label attribute to cells for modal display
 * Add "mobile-details-trigger" class to a button in the actions column
 */
export default function ResponsiveTable({ children, className = '' }: ResponsiveTableProps) {
  const [showModal, setShowModal] = useState(false)
  const [modalData, setModalData] = useState<{ label: string; value: ReactNode }[]>([])
  const [modalTitle, setModalTitle] = useState('')
  const [hiddenColumns, setHiddenColumns] = useState<number[]>([])
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const checkingRef = useRef(false)
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const container = tableContainerRef.current
    if (!container) return

    const checkColumnVisibility = () => {
      // Prevent re-entrant calls
      if (checkingRef.current) return
      checkingRef.current = true

      const table = container.querySelector('table')
      if (!table) {
        checkingRef.current = false
        return
      }

      // Use requestAnimationFrame to ensure DOM has settled
      requestAnimationFrame(() => {
        const tableWidth = table.offsetWidth
        const containerWidth = container.offsetWidth
        const isOverflowing = tableWidth > containerWidth

        // Get all columns with priority
        const headerCells = Array.from(table.querySelectorAll('thead tr th.hide-mobile[data-priority]'))

        if (!isOverflowing) {
          // Not overflowing, show all columns
          setHiddenColumns(prev => prev.length > 0 ? [] : prev)
          checkingRef.current = false
          return
        }

        if (isOverflowing) {
          // Sort by priority (lower number = hide first)
          const sortedColumns = headerCells
            .map((cell) => ({
              index: Array.from(table.querySelectorAll('thead tr th')).indexOf(cell),
              priority: parseInt(cell.getAttribute('data-priority') || '999', 10)
            }))
            .sort((a, b) => a.priority - b.priority)

          // Progressively hide columns until table fits or all hideable columns are hidden
          let currentHidden: number[] = []
          for (const col of sortedColumns) {
            currentHidden.push(col.index)

            // Temporarily hide these columns to check if it fits
            const rows = table.querySelectorAll('tr')
            rows.forEach(row => {
              const cells = Array.from(row.children)
              cells.forEach((cell, idx) => {
                if (currentHidden.includes(idx)) {
                  (cell as HTMLElement).style.display = 'none'
                }
              })
            })

            const newTableWidth = table.offsetWidth

            // Restore visibility for measurement
            rows.forEach(row => {
              const cells = Array.from(row.children)
              cells.forEach((cell) => {
                (cell as HTMLElement).style.display = ''
              })
            })

            if (newTableWidth <= containerWidth) {
              // Found the right amount to hide - only update if different
              const newHidden = [...currentHidden]
              setHiddenColumns(prev => {
                if (JSON.stringify(prev) !== JSON.stringify(newHidden)) {
                  return newHidden
                }
                return prev
              })
              checkingRef.current = false
              return
            }
          }

          // Hide all hideable columns if still overflowing
          const allHidden = sortedColumns.map(c => c.index)
          setHiddenColumns(prev => {
            if (JSON.stringify(prev) !== JSON.stringify(allHidden)) {
              return allHidden
            }
            return prev
          })
        }

        checkingRef.current = false
      })
    }

    const debouncedCheck = () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      debounceTimeoutRef.current = setTimeout(checkColumnVisibility, 100)
    }

    // Initial check with slight delay to ensure DOM is ready
    const initialTimeout = setTimeout(checkColumnVisibility, 100)

    // Create ResizeObserver to monitor container width changes
    resizeObserverRef.current = new ResizeObserver(debouncedCheck)
    resizeObserverRef.current.observe(container)

    // Also listen to window resize
    window.addEventListener('resize', debouncedCheck)

    return () => {
      clearTimeout(initialTimeout)
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }
      window.removeEventListener('resize', debouncedCheck)
    }
  }, []) // Empty dependency array - only run once

  const handleDetailsClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (!target.closest('.mobile-details-trigger')) return

    const row = target.closest('tr')
    if (!row) return

    // Get all hidden cells in this row
    const cells = Array.from(row.querySelectorAll('td'))
    const data: { label: string; value: ReactNode }[] = []

    cells.forEach((cell, index) => {
      if (hiddenColumns.includes(index) && cell.hasAttribute('data-label')) {
        const label = cell.getAttribute('data-label') || ''
        const value = cell.textContent || ''
        if (label && value) {
          data.push({ label, value })
        }
      }
    })

    // Get row title from first cell
    const firstCell = row.querySelector('td:first-child')
    const title = firstCell?.textContent?.trim() || 'Details'

    setModalTitle(title)
    setModalData(data)
    setShowModal(true)
  }

  return (
    <>
      <style>{`
        .responsive-table-wrapper {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        /* Override the default .table min-w-full to allow horizontal scroll */
        .responsive-table-wrapper table {
          width: 100%;
          min-width: unset !important;
        }

        /* Hide columns based on dynamic detection */
        ${hiddenColumns.map(index => `
          .responsive-table-wrapper table tr > *:nth-child(${index + 1}) {
            display: none !important;
          }
        `).join('\n')}

        /* Show details button when columns are hidden */
        .mobile-details-trigger {
          display: ${hiddenColumns.length > 0 ? 'inline-flex' : 'none'} !important;
          align-items: center;
          justify-content: center;
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          color: #2563eb;
          background-color: #eff6ff;
          border-radius: 0.375rem;
          margin-right: 0.5rem;
          transition: background-color 0.2s;
        }

        .mobile-details-trigger:hover {
          background-color: #dbeafe;
        }

        .mobile-details-trigger svg {
          width: 1rem;
          height: 1rem;
        }

        /* Reduce padding on smaller screens */
        @media (max-width: 1024px) {
          .responsive-table-wrapper table thead tr th,
          .responsive-table-wrapper table tbody tr td {
            padding: 0.75rem 0.5rem;
          }
        }

        /* First column should have min width */
        .responsive-table-wrapper table thead tr th:first-child,
        .responsive-table-wrapper table tbody tr td:first-child {
          min-width: 120px;
        }

        /* Last column (actions) should not wrap */
        .responsive-table-wrapper table thead tr th:last-child,
        .responsive-table-wrapper table tbody tr td:last-child {
          white-space: nowrap;
        }
      `}</style>
      <div className={`relative ${className}`}>
        {/* Responsive table container */}
        <div
          ref={tableContainerRef}
          className="responsive-table-wrapper -mx-4 px-4 md:mx-0 md:px-0"
          onClick={handleDetailsClick}
        >
          {children}
        </div>
      </div>

      {/* Details Modal */}
      {showModal && (
        <Modal
          title={modalTitle}
          onClose={() => setShowModal(false)}
        >
          <div className="space-y-4">
            {modalData.map((item, index) => (
              <div key={index} className="border-b border-gray-200 pb-3 last:border-0">
                <dt className="text-sm font-medium text-gray-500 mb-1">{item.label}</dt>
                <dd className="text-sm text-gray-900">{item.value}</dd>
              </div>
            ))}
            {modalData.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No additional details available</p>
            )}
          </div>
        </Modal>
      )}
    </>
  )
}
